import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';

import { useUserId } from '../../../app/hooks/accounts';
import {
  useDeleteCollection,
  useGetCollection,
  usePatchCollection,
  useSaveView,
  useUpdateView,
} from '../../../app/hooks/view';
import { useTabManager } from '../../context/tab-manager-context';
import { storeViewState } from '../../utils/storage';
import { getTabDisplayName } from '../../utils/tab-display';
import { constructPersistedView } from '../../utils/view-state-helpers';

import { ConfirmCloseModal } from './ConfirmCloseModal';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { SaveCollectionModal } from './SaveCollectionModal';
import { ShareCollectionLinkModal } from './ShareCollectionLinkModal';
import { ViewTooltipComponent } from './ViewTooltipComponent';

import {
  Dropdown,
  Icon,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
  useToast,
} from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { ViewShareType } from '@controlrooms/models';

const CollectionTabContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 35px;
  padding: 0 8px;
  cursor: default;
  max-width: 200px;
  min-width: 150px;
  flex-shrink: 0;
  background-color: transparent;
  border-bottom: 2px solid ${({ theme }) => theme.v2.tab.inactiveBorderColor};
  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.hoverBackgroundColor};
  }
  &:hover .collection-tab-actions {
    opacity: 1;
    pointer-events: auto;
  }
`;

const TabContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  flex-wrap: nowrap;
  padding-right: 0;
`;

const IconWrapper = styled.span`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DirtyIndicator = styled.span`
  width: 16px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.v2.tab.dirtyIndicatorColor};
  text-align: center;
`;

const CollectionName = styled.span`
  font-family: 'Open Sans';
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;
  color: ${({ theme }) => theme.v2.tab.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const ActionsWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 4px;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  padding-left: 32px;
  background: linear-gradient(
    to right,
    transparent 0%,
    ${({ theme }) => theme.v2.tab.hoverBackgroundColor || 'rgba(255,255,255,0.05)'} 60%,
    ${({ theme }) =>
        theme.v2.tab.hoverBackgroundColor || theme.v2.tab.containerBackgroundColor || 'inherit'}
      100%
  );
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
`;

const CloseButton = styled.button`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.dropdownHoverColor};
  }
`;

const ChevronButton = styled.button`
  width: 20px;
  height: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.dropdownHoverColor};
    border-radius: 4px;
  }
`;

export const CollectionTab = () => {
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [confirmCloseModal, setConfirmCloseModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null,
  );

  const {
    tabs,
    viewCollection,
    setViewCollection,
    closeAllTabs,
    closeCollection,
    getViewState,
    replaceTabIdWithViewId,
    updateTabMetadata,
    updateActiveTabViewState,
  } = useTabManager();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentUserId = useUserId();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [showManageCollectionModal, setShowManageCollectionModal] = useState(false);

  const { mutateAsync: patchCollection } = usePatchCollection();
  const { mutateAsync: deletePersistedView } = useDeleteCollection();
  const { mutateAsync: createView } = useSaveView();
  const { mutateAsync: updateView } = useUpdateView();
  const { data: currentCollection } = useGetCollection(viewCollection.collectionId as string);

  // Handle click outside for portal dropdown
  useEffect(() => {
    if (!openSubMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking on the dropdown content
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on the trigger
      if (triggerRef.current && triggerRef.current.contains(target)) {
        return;
      }

      // Close if clicking anywhere else
      setOpenSubMenu(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openSubMenu]);

  const handlePatch = useCallback(async () => {
    const collection_id = viewCollection.collectionId;
    const currentCollectionViewIds =
      currentCollection?.views?.map((v) => v.view_id as string) || [];

    const savedDirtyTabs = tabs.filter(
      (t): t is typeof t & { viewId: string } => t.saved && !!t.viewId && !!t.isDirty,
    );
    const unsavedViewTabs = tabs.filter((t) => !t.saved);

    const effectiveUserId = currentUserId ?? 0;
    let someViewsFailedToSave = false;

    // Update all saved dirty tabs (PUT) so their backend state matches before patching collection
    for (const tab of savedDirtyTabs) {
      try {
        const viewState = await getViewState(tab.id);
        const persistView = constructPersistedView({
          viewState,
          viewName: tab.name || '',
          viewDescription: tab.description ?? '',
          currentUserId: effectiveUserId,
          shareType: (viewState.shareType ?? ViewShareType.PRIVATE) as ViewShareType,
          shared: viewState.shareType !== ViewShareType.PRIVATE,
        });
        await updateView({ view_id: tab.viewId, view: persistView });
        const updatedViewState = {
          ...viewState,
          name: tab.name,
          description: tab.description ?? '',
          isDirty: false,
        };
        await updateActiveTabViewState({ ...updatedViewState, viewId: tab.id });
      } catch (error) {
        console.error('Failed to update dirty view before collection patch:', error);
        someViewsFailedToSave = true;
        updateTabMetadata(tab.id, { isDirty: true });
        getViewState(tab.id)
          .then((vs) => storeViewState(tab.id, { ...vs, isDirty: true }))
          .catch(() => undefined);
      }
    }

    // Save all unsaved VIEWS tabs via create endpoint, then add their ids to the collection
    const newlySavedViewIds: string[] = [];

    for (const tab of unsavedViewTabs) {
      try {
        const viewState = await getViewState(tab.id);
        const baseName = getTabDisplayName(tab);
        const displayName = `${baseName} ${Date.now()}`;
        const persistView = constructPersistedView({
          viewState,
          viewName: displayName,
          viewDescription: tab.description ?? '',
          currentUserId: effectiveUserId,
          shareType: ViewShareType.PRIVATE,
          shared: false,
        });
        const viewId = await createView(persistView);
        const updatedViewState = {
          ...viewState,
          view_id: viewId,
          viewId,
          name: displayName,
          description: tab.description ?? '',
          shareType: ViewShareType.PRIVATE,
          shared: false,
          isDirty: false,
        };
        await replaceTabIdWithViewId(
          tab.id,
          viewId,
          {
            saved: true,
            viewId,
            userId: effectiveUserId,
            name: displayName,
            description: tab.description ?? '',
            isDirty: false,
          },
          updatedViewState,
        );
        newlySavedViewIds.push(viewId);
      } catch (error) {
        console.error('Failed to save unsaved view before collection patch:', error);
        someViewsFailedToSave = true;
        updateTabMetadata(tab.id, { isDirty: true });
        getViewState(tab.id)
          .then((vs) => storeViewState(tab.id, { ...vs, isDirty: true }))
          .catch(() => undefined);
      }
    }

    if (someViewsFailedToSave) {
      toast.error(
        'Unable to save some views while updating the collection. Please retry saving the tabs with dirty state.',
      );
    }

    // Only include saved view IDs in add_views (existing saved + newly saved)
    const existingSavedViewIds = tabs
      .filter((t): t is typeof t & { viewId: string } => t.saved && !!t.viewId)
      .map((t) => t.viewId);
    const allSavedViewIds = [...existingSavedViewIds, ...newlySavedViewIds];
    const newViewIds = allSavedViewIds.filter((id) => !currentCollectionViewIds.includes(id));
    // Remove from collection any view not in our current (post-save) tab set
    const deletedViewIds = currentCollectionViewIds.filter((id) => !allSavedViewIds.includes(id));

    const hasAddOrRemove = newViewIds.length > 0 || deletedViewIds.length > 0;
    try {
      if (collection_id) {
        if (hasAddOrRemove) {
          try {
            await patchCollection({
              collection_id: collection_id,
              collection: {
                collection_name: viewCollection.name || '',
                description: viewCollection.description || '',
                add_views: newViewIds,
                remove_views: deletedViewIds,
              },
            });
            queryClient.refetchQueries(['user-collections', currentUserId]);
            queryClient.refetchQueries(['collection', collection_id]);
          } catch (error) {
            console.error(error);
          }
        }
        setViewCollection((prev) => ({ ...prev, isDirty: false }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setOpenSubMenu(false);
    }
  }, [
    currentCollection?.views,
    currentUserId,
    createView,
    getViewState,
    patchCollection,
    queryClient,
    replaceTabIdWithViewId,
    setViewCollection,
    tabs,
    toast,
    updateActiveTabViewState,
    updateTabMetadata,
    updateView,
    viewCollection.collectionId,
    viewCollection.description,
    viewCollection.name,
  ]);

  const handleDelete = useCallback(async () => {
    const collection_id = viewCollection.collectionId;
    if (collection_id) {
      await deletePersistedView(collection_id);
    }
  }, [deletePersistedView, viewCollection.collectionId]);

  if (!viewCollection.name || viewCollection.name === '') {
    return null;
  }

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (openSubMenu) {
      setOpenSubMenu(false);
    } else if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setOpenSubMenu(true);
    }
  };

  return (
    <CollectionTabContainer>
      <TabContent>
        {viewCollection.isDirty && <DirtyIndicator>*</DirtyIndicator>}
        <IconWrapper>
          <Icon name={ICONS.Folder} width="14" height="14" />
        </IconWrapper>
        <Tooltip
          label={
            <ViewTooltipComponent
              name={viewCollection.name}
              description={viewCollection.description || ''}
              createdBy={viewCollection.createdBy || ''}
            />
          }
          theme={'dark'}
        >
          <CollectionName title={viewCollection.name}>{viewCollection.name}</CollectionName>
        </Tooltip>
      </TabContent>
      <ActionsWrapper className="collection-tab-actions">
        <CloseButton
          type="button"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setConfirmCloseModal(true);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label="Close collection"
          data-testid="collection-tab-close"
        >
          <Icon name={ICONS.Close} width="10" height="10" color="#A0A38D" />
        </CloseButton>
        <Dropdown.Root isOpen={openSubMenu}>
          <ChevronButton
            type="button"
            ref={triggerRef}
            onClick={handleChevronClick}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label="Collection menu"
            data-testid="collection-tab-menu"
          >
            <Icon name={ICONS.Chevron} width="10" color="#A0A38D" />
          </ChevronButton>
          {openSubMenu &&
            dropdownPosition &&
            ReactDOM.createPortal(
              <div ref={dropdownRef}>
                <Dropdown.Options
                  position="right"
                  open={openSubMenu}
                  width="170px"
                  style={{
                    position: 'absolute',
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    zIndex: 9999,
                  }}
                >
                  <Dropdown.Item
                    disabled={!viewCollection.isDirty}
                    onClick={() => {
                      if (viewCollection.isDirty) {
                        setOpenSubMenu(false);
                        const hasUnsavedViews =
                          tabs.some((t) => !t.saved) || tabs.some((t) => t.saved && !!t.isDirty);
                        if (hasUnsavedViews) {
                          setShowSaveConfirmModal(true);
                        } else {
                          handlePatch();
                        }
                      }
                    }}
                  >
                    Save Changes
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setShowManageCollectionModal(true);
                      setOpenSubMenu(false);
                    }}
                  >
                    Manage
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setConfirmDeleteModal(true);
                      setOpenSubMenu(false);
                    }}
                  >
                    Delete
                  </Dropdown.Item>
                  <Dropdown.Separator />
                  <Dropdown.Item
                    onClick={() => {
                      setConfirmCloseModal(true);
                      setOpenSubMenu(false);
                    }}
                  >
                    Close
                  </Dropdown.Item>
                  {!!viewCollection?.viewsIds.length && (
                    <Dropdown.Item
                      onClick={() => {
                        setShowShareModal(true);
                        setOpenSubMenu(false);
                      }}
                    >
                      Share
                    </Dropdown.Item>
                  )}
                </Dropdown.Options>
              </div>,
              document.body,
            )}
        </Dropdown.Root>
      </ActionsWrapper>
      {showShareModal && !!viewCollection?.viewsIds.length && (
        <ShareCollectionLinkModal
          onClose={() => {
            setShowShareModal(false);
          }}
        />
      )}
      {showManageCollectionModal ? (
        <SaveCollectionModal
          onClose={() => setShowManageCollectionModal(false)}
          isUpdateCollection={true}
        />
      ) : null}
      {confirmCloseModal && (
        <ConfirmCloseModal
          entityType="collection"
          onConfirm={async (state) => {
            if (state) {
              closeAllTabs();
              closeCollection();
            }
            setConfirmCloseModal(false);
          }}
        />
      )}
      {confirmDeleteModal && (
        <ConfirmDeleteModal
          onConfirm={async (state) => {
            setConfirmDeleteModal(false);
            if (state) {
              // Close immediately - UI updates right away
              closeAllTabs();
              closeCollection();
              // Then perform deletion in background
              try {
                await handleDelete();
              } catch (error) {
                console.error('Failed to delete collection:', error);
                // If deletion fails, we've already closed, which is fine
              }
            }
          }}
        />
      )}
      {showSaveConfirmModal && (
        <Modal open={true} onCancel={() => setShowSaveConfirmModal(false)} size="sm">
          <ModalHeader title="Save collection changes" />
          <ModalBody>
            <div style={{ padding: '10px', fontSize: '13px' }}>
              <p>
                There are some tabs which are unsaved. This action would save all the unsaved tabs
                and add them to the current tab set.
              </p>
            </div>
          </ModalBody>
          <ModalFooter
            confirmText="Confirm"
            cancelText="Cancel"
            onConfirm={async () => {
              setShowSaveConfirmModal(false);
              await handlePatch();
            }}
          />
        </Modal>
      )}
    </CollectionTabContainer>
  );
};
